import React, { useState } from 'react';
import { Terminal, Server, Plus, Copy, Shield, Zap, Code, Monitor, Globe, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const SSHConnection: React.FC = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connecting' | 'connected' | 'failed'>('idle');

  const handleStartSSH = async () => {
    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    // 模拟启动过程
    setTimeout(() => {
      setConnectionStatus('connected');
      setIsConnecting(false);
      toast.success('SSH节点启动成功！');
    }, 3000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('已复制到剪贴板');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* 页面标题 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-r from-slate-700 via-gray-700 to-slate-800 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Terminal className="w-7 h-7 text-white" />
              </div>
              <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                connectionStatus === 'connected' 
                  ? 'bg-gradient-to-r from-green-400 to-emerald-500 animate-pulse' 
                  : 'bg-gradient-to-r from-red-400 to-rose-500'
              }`}>
                <Monitor className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-700 to-gray-800 bg-clip-text text-transparent leading-tight">
                SSH连接
              </h1>
              <p className="text-gray-600 mt-2 text-base leading-relaxed">通过SSH安全连接到计算集群</p>
            </div>
          </div>
          
          <button 
            onClick={handleStartSSH}
            disabled={isConnecting || connectionStatus === 'connected'}
            className="group flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl hover:from-slate-800 hover:to-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {isConnecting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>启动中...</span>
              </>
            ) : connectionStatus === 'connected' ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>已连接</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                <span>启动SSH节点</span>
              </>
            )}
          </button>
        </div>

        {/* 状态指示器 */}
        {connectionStatus !== 'idle' && (
          <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${
                connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                connectionStatus === 'connected' ? 'bg-green-500' :
                'bg-red-500'
              }`}></div>
              <span className="font-medium text-gray-900">
                {connectionStatus === 'connecting' ? '正在启动SSH节点...' :
                 connectionStatus === 'connected' ? 'SSH节点已就绪' :
                 'SSH节点启动失败'}
              </span>
            </div>
          </div>
        )}

        {/* 主要内容区域 */}
        <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-8 shadow-lg">
          {/* 连接示意图 */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-12">
              {/* 用户设备 */}
              <div className="text-center group">
                <div className="w-20 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-1">
                  <Monitor className="w-10 h-8 text-blue-600" />
                </div>
                <div className="text-sm font-medium text-gray-700">您的设备</div>
              </div>
              
              {/* 连接动画 */}
              <div className="flex items-center space-x-3">
                <div className="w-8 h-1 bg-gradient-to-r from-blue-400 to-transparent rounded-full"></div>
                <div className={`w-4 h-4 rounded-full border-2 border-green-400 ${connectionStatus === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-white'} shadow-lg`}></div>
                <div className="w-8 h-1 bg-gradient-to-l from-blue-400 to-transparent rounded-full"></div>
              </div>
              
              {/* 服务器 */}
              <div className="text-center group">
                <div className="w-20 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mb-3 shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-1">
                  <Server className="w-10 h-8 text-slate-700" />
                </div>
                <div className="text-sm font-medium text-gray-700">计算集群</div>
              </div>
            </div>
          </div>

          {/* 功能特点 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">安全连接</h4>
              <p className="text-sm text-gray-600">基于SSH协议的加密连接，确保数据传输安全</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">高性能</h4>
              <p className="text-sm text-gray-600">直接访问计算资源，获得最佳性能体验</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Code className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">命令行操作</h4>
              <p className="text-sm text-gray-600">完整的Shell环境，支持所有Linux命令</p>
            </div>
          </div>

          {/* 说明文字 */}
          <div className="text-center">
            <p className="text-gray-600 leading-relaxed">
              该功能适用于命令行提交作业，启动管理节点，集群自动配置。<br />
              通过Web SSH连接管理节点安装软件，提交作业，管理文件。
            </p>
          </div>
        </div>

        {/* 连接信息和常用命令 */}
        {connectionStatus === 'connected' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SSH连接信息 */}
            <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-green-700 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">连接信息</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">SSH连接命令</label>
                  <div className="group relative">
                    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-4 border border-slate-700">
                      <code className="text-sm text-green-400 font-mono">ssh username@login.chaincloud.com</code>
                    </div>
                    <button 
                      onClick={() => copyToClipboard('ssh username@login.chaincloud.com')}
                      className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all duration-200"
                    >
                      <Copy className="w-3 h-3 text-gray-300" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">端口</label>
                  <div className="group relative">
                    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-4 border border-slate-700">
                      <code className="text-sm text-green-400 font-mono">22</code>
                    </div>
                    <button 
                      onClick={() => copyToClipboard('22')}
                      className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all duration-200"
                    >
                      <Copy className="w-3 h-3 text-gray-300" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">节点状态</label>
                  <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-800">节点运行中</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 常用命令 */}
            <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg flex items-center justify-center">
                  <Terminal className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">常用命令</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  { cmd: 'squeue', desc: '查看作业队列', color: 'from-blue-500 to-blue-600' },
                  { cmd: 'sbatch', desc: '提交作业', color: 'from-green-500 to-green-600' },
                  { cmd: 'scancel', desc: '取消作业', color: 'from-red-500 to-red-600' },
                  { cmd: 'sinfo', desc: '查看节点信息', color: 'from-purple-500 to-purple-600' }
                ].map((item, index) => (
                  <div key={item.cmd} className="group flex justify-between items-center p-4 bg-gradient-to-r from-gray-50/80 to-white/80 rounded-xl border border-gray-200/50 hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5" style={{animationDelay: `${index * 100}ms`}}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 bg-gradient-to-r ${item.color} rounded-lg flex items-center justify-center`}>
                        <Terminal className="w-3 h-3 text-white" />
                      </div>
                      <code className="text-sm font-mono text-gray-800 font-semibold">{item.cmd}</code>
                    </div>
                    <span className="text-xs text-gray-600 font-medium">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 使用说明 */}
        <div className="backdrop-blur-sm bg-white/70 border border-white/20 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">使用说明</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                <p className="text-gray-700">点击"启动SSH节点"按钮创建专用的SSH访问节点</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                <p className="text-gray-700">节点启动后，使用提供的SSH命令连接到计算集群</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                <p className="text-gray-700">通过命令行界面管理作业、上传文件和安装软件</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">4</div>
                <p className="text-gray-700">使用Slurm作业调度系统提交和管理计算任务</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSHConnection;
